import React, { useEffect, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Link,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";
import PrivacyTipOutlinedIcon from "@mui/icons-material/PrivacyTipOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { WarningRounded } from "@mui/icons-material";

const JoinConsultationDialog = ({
  open,

  skippedDeviceCheck = false,
  deviceCheckHasIssues = false,

  onClose,
  onReturnToDeviceCheck,
  onEnterConsultation,

  entering = false,

  privacyPolicyUrl = null,
  termsUrl = null,
}) => {
  const [agreementAccepted, setAgreementAccepted] = useState(false);

  const theme = useTheme();

  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  /*
   * A new dialog opening should always
   * require a fresh acknowledgement.
   */
  useEffect(() => {
    if (open) {
      setAgreementAccepted(false);
    }
  }, [open]);

  const handleEnter = () => {
    if (!agreementAccepted || entering) {
      return;
    }

    onEnterConsultation?.();
  };

  return (
    <Dialog
      open={Boolean(open)}
      onClose={entering ? undefined : onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
      aria-labelledby="join-consultation-dialog-title"
      PaperProps={{
        sx: {
          width: {
            xs: "100%",
            sm: "calc(100% - 48px)",
          },

          maxWidth: {
            sm: 620,
          },

          maxHeight: {
            xs: "100%",
            sm: "90vh",
          },

          m: {
            xs: 0,
            sm: 2,
          },

          borderRadius: {
            xs: 0,
            sm: 3,
          },

          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        id="join-consultation-dialog-title"
        sx={{
          px: {
            xs: 2,
            sm: 3,
          },

          py: {
            xs: 1.75,
            sm: 2,
          },
        }}
      >
        Before You Join
      </DialogTitle>

      <Divider />

      <DialogContent
        sx={{
          px: {
            xs: 2,
            sm: 3,
          },

          py: {
            xs: 2,
            sm: 2.5,
          },

          overflowY: "auto",
        }}
      >
        <Stack spacing={2.5}>
          {skippedDeviceCheck && (
            <Alert
              severity="info"
              icon={<DevicesOutlinedIcon />}
              action={
                <Button
                  size="small"
                  onClick={onReturnToDeviceCheck}
                  startIcon={<WarningRounded />}
                  disabled={entering}

                >
                  Check Devices
                </Button>
              }
            >
              <Typography variant="body2" fontWeight={700}>
                Device check not completed
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                }}
              >
                We recommend checking your camera, microphone and speakers
                before joining. You can still continue without the test.
              </Typography>
            </Alert>
          )}

          {!skippedDeviceCheck && deviceCheckHasIssues && (
            <Alert
              severity="warning"
              icon={<WarningAmberOutlinedIcon />}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={onReturnToDeviceCheck}
                  disabled={entering}
                  sx={{
                    whiteSpace: "nowrap",
                  }}
                >
                  Check Again
                </Button>
              }
            >
              <Typography variant="body2" fontWeight={700}>
                Some devices may not be ready
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                }}
              >
                Audio or video may not work correctly. You can return to the
                waiting room and fix the issue, or continue if you understand
                the risk.
              </Typography>
            </Alert>
          )}

          {!skippedDeviceCheck && !deviceCheckHasIssues && (
            <Alert severity="success" icon={<CheckCircleOutlineOutlinedIcon />}>
              Your device check is complete.
            </Alert>
          )}

          <Box>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                mb: 1,
              }}
            >
              <PrivacyTipOutlinedIcon color="primary" />

              <Typography variant="subtitle1" fontWeight={700}>
                Privacy & Respect
              </Typography>
            </Stack>

            <Typography variant="body2" color="text.secondary">
              Please protect your privacy and respect everyone taking part in
              the consultation.
            </Typography>
          </Box>

          <Stack spacing={1.25}>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "flex-start",
              }}
            >
              <ShieldOutlinedIcon
                color="primary"
                fontSize="small"
                sx={{
                  mt: 0.2,
                }}
              />

              <Typography variant="body2">
                Do not record, photograph, screenshot, livestream or share the
                consultation unless proper permission has been given.
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "flex-start",
              }}
            >
              <ShieldOutlinedIcon
                color="primary"
                fontSize="small"
                sx={{
                  mt: 0.2,
                }}
              />

              <Typography variant="body2">
                Do not threaten, harass, abuse, intimidate or deliberately
                disrupt another participant.
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "flex-start",
              }}
            >
              <ShieldOutlinedIcon
                color="primary"
                fontSize="small"
                sx={{
                  mt: 0.2,
                }}
              />

              <Typography variant="body2">
                Keep personal and medical information private and do not share
                another person's information without permission.
              </Typography>
            </Box>
          </Stack>

          <Alert severity="warning">
            Serious misuse may cause the consultation to be ended and may be
            reviewed according to the platform's policies and applicable
            procedures.
          </Alert>

          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreementAccepted}
                  onChange={(event) =>
                    setAgreementAccepted(event.target.checked)
                  }
                  disabled={entering}
                />
              }
              label={
                <Typography variant="body2">
                  I have read and agree to follow these consultation privacy and
                  conduct rules.
                </Typography>
              }
              sx={{
                m: 0,

                alignItems: "flex-start",

                "& .MuiCheckbox-root": {
                  pt: 0.25,
                  pl: 0,
                },

                "& .MuiFormControlLabel-label": {
                  flex: 1,
                },
              }}
            />

            {(privacyPolicyUrl || termsUrl) && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",

                  ml: {
                    xs: 0,
                    sm: 4,
                  },

                  mt: 0.75,
                }}
              >
                {privacyPolicyUrl && (
                  <Link
                    href={privacyPolicyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </Link>
                )}

                {privacyPolicyUrl && termsUrl && " · "}

                {termsUrl && (
                  <Link
                    href={termsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms of Use
                  </Link>
                )}
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: {
            xs: 2,
            sm: 3,
          },

          py: {
            xs: 1.5,
            sm: 2,
          },

          gap: 1,

          flexDirection: {
            xs: "column-reverse",
            sm: "row",
          },

          alignItems: {
            xs: "stretch",
            sm: "center",
          },

          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Button
          variant="text"
          onClick={onClose}
          disabled={entering}
          fullWidth={fullScreen}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleEnter}
          disabled={!agreementAccepted || entering}
          fullWidth={fullScreen}
        >
          {entering ? "Entering..." : "Enter Consultation"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JoinConsultationDialog;
